import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stack,
  Switch,
  Text,
  Paper,
  Group,
  Box,
  Loader,
  Divider,
  Accordion,
  Alert,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Lock } from 'react-feather';
import { cohortsApi } from '../../../../services/api/cohorts';
import type { FeatureToggles, SenderEmailSettings } from '../../../../services/api/cohorts';
import type { CohortEmailSettings, EmailPersona } from '../../../../types';
import {
  PERSONA_CONFIG,
  EMAIL_CONFIG_BY_PERSONA,
} from '../../../../config/emailSettingsConfig';
import type { SectionRegister } from '../edit-cohort/sectionContract';

interface EmailSettingsSectionProps {
  cohortId: string;
  /** When provided by parent, overrides the internally-fetched feature toggles.
   * MED-1269: this is now the parent's PENDING toggle intent, so the lock UI
   * reflects what the user is about to save (AC15). */
  externalFeatureToggles?: FeatureToggles | null;
  /** MED-1269: register this section's deferred-save controller with the modal. */
  registerSection?: SectionRegister;
}

// Map persona key to the corresponding feature toggle
const PERSONA_TO_FEATURE_TOGGLE: Record<string, keyof FeatureToggles> = {
  participants: 'participant_features_enabled',
  coaches: 'coach_features_enabled',
};

export function EmailSettingsSection({
  cohortId,
  externalFeatureToggles,
  registerSection,
}: EmailSettingsSectionProps) {
  // `saved` is the as-loaded baseline; `settings` is pending intent (MED-1269).
  const [saved, setSaved] = useState<CohortEmailSettings | null>(null);
  const [settings, setSettings] = useState<CohortEmailSettings | null>(null);
  const [internalFeatureToggles, setInternalFeatureToggles] =
    useState<FeatureToggles | null>(null);
  // Use external toggles if provided, otherwise use internally-fetched ones
  const featureToggles = externalFeatureToggles ?? internalFeatureToggles;
  const [loading, setLoading] = useState(true);
  // MED-1307: sender address. `senderSaved` is the as-loaded value, `senderInput`
  // is what the CM typed. Empty input means "use the default".
  const [senderSettings, setSenderSettings] = useState<SenderEmailSettings | null>(null);
  const [senderInput, setSenderInput] = useState('');
  const [senderError, setSenderError] = useState<string | null>(null);
  const senderSaved = senderSettings?.sender_email ?? '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // MED-1307: the sender request is separate, so its failure leaves
        // senderSettings null and does not fail the email settings.
        const senderRequest = cohortsApi.getSenderEmail(cohortId).catch(() => null);
        const [emailSettings, toggles] = await Promise.all([
          cohortsApi.getEmailSettings(cohortId),
          cohortsApi.getFeatureToggles(cohortId),
        ]);
        const sender = await senderRequest;
        setSaved(emailSettings);
        setSettings(emailSettings);
        setInternalFeatureToggles(toggles);
        setSenderSettings(sender);
        setSenderInput(sender?.sender_email ?? '');
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Failed to load email settings',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cohortId]);

  const handleMasterToggle = useCallback((checked: boolean) => {
    setSettings(prev => (prev ? { ...prev, master_enabled: checked } : prev));
  }, []);

  const handlePersonaToggle = useCallback(
    (persona: EmailPersona, checked: boolean) => {
      setSettings(prev =>
        prev
          ? {
              ...prev,
              personas: {
                ...prev.personas,
                [persona]: { ...prev.personas[persona], enabled: checked },
              },
            }
          : prev
      );
    },
    []
  );

  const handleEmailTypeToggle = useCallback(
    (persona: EmailPersona, emailType: string, checked: boolean) => {
      setSettings(prev =>
        prev
          ? {
              ...prev,
              personas: {
                ...prev.personas,
                [persona]: {
                  ...prev.personas[persona],
                  emails: {
                    ...prev.personas[persona].emails,
                    [emailType]: checked,
                  },
                },
              },
            }
          : prev
      );
    },
    []
  );

  // Stable controller for the modal: refs hold the latest pending/baseline.
  const settingsRef = useRef(settings);
  const savedRef = useRef(saved);
  settingsRef.current = settings;
  savedRef.current = saved;
  const senderInputRef = useRef(senderInput);
  const senderSavedRef = useRef(senderSaved);
  senderInputRef.current = senderInput;
  senderSavedRef.current = senderSaved;
  const senderLoadedRef = useRef(senderSettings !== null);
  senderLoadedRef.current = senderSettings !== null;

  const senderSettingsRef = useRef(senderSettings);
  senderSettingsRef.current = senderSettings;

  // MED-1307: block the save when the typed address cannot be accepted.
  const validate = useCallback((): boolean => {
    const sender = senderSettingsRef.current;
    const typed = senderInputRef.current.trim().toLowerCase();
    if (!sender || typed === '') return true;
    const match = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(typed);
    const domain = typed.slice(typed.lastIndexOf('@') + 1);
    if (match && sender.allowed_domains.includes(domain)) return true;
    setSenderError(
      `Use an address at ${sender.allowed_domains.join(', ')}, or leave this empty to use ${sender.default_sender_email}.`
    );
    return false;
  }, []);

  const commit = useCallback(async () => {
    const pending = settingsRef.current;
    if (pending && JSON.stringify(pending) !== JSON.stringify(savedRef.current)) {
      const result = await cohortsApi.updateEmailSettings(cohortId, pending);
      setSaved(result);
      setSettings(result);
    }
    // MED-1307: compare trimmed values, so trailing spaces alone are not a change.
    const senderPending = senderInputRef.current.trim();
    if (senderLoadedRef.current && senderPending !== senderSavedRef.current) {
      try {
        const result = await cohortsApi.updateSenderEmail(cohortId, senderPending || null);
        setSenderSettings(result);
        setSenderInput(result.sender_email ?? '');
        setSenderError(null);
      } catch (err) {
        // Keep the typed value so the CM can correct it (sectionContract AC19).
        setSenderError(err instanceof Error ? err.message : 'Could not save the sender address');
        throw err;
      }
    }
  }, [cohortId]);

  useEffect(() => {
    if (!registerSection || !settings || !saved) return;
    const dirty =
      JSON.stringify(settings) !== JSON.stringify(saved) ||
      (senderSettings !== null && senderInput.trim() !== senderSaved);
    // Email settings changes are non-destructive — no save-time summary entries.
    registerSection({ dirty, destructive: [], commit, validate });
    return () => registerSection(null);
  }, [settings, saved, senderInput, senderSaved, senderSettings, registerSection, commit, validate]);

  /** Check if a persona's toggles are locked by the parent feature toggle */
  const isPersonaLockedByFeatureToggle = (persona: EmailPersona): boolean => {
    if (!featureToggles) return false;
    const toggleKey = PERSONA_TO_FEATURE_TOGGLE[persona];
    if (!toggleKey) return false; // 'partners' has no feature toggle
    return !featureToggles[toggleKey];
  };

  return (
    <Accordion.Item value="email-settings">
      <Accordion.Control>
        <Group gap="sm">
          <Text fw={600}>Email Settings</Text>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        {loading ? (
          <Stack align="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading email settings...
            </Text>
          </Stack>
        ) : !settings ? null : (
          <Stack gap="lg">
            <Text size="sm" c="dimmed">
              Control which automated emails are sent for this cohort. Changes
              are saved when you click Save Changes.
            </Text>

            {/* Master toggle */}
            <Paper p="md" radius="md" withBorder>
              <Group justify="space-between" align="center">
                <Box>
                  <Text fw={600}>All Emails</Text>
                  <Text size="sm" c="dimmed">
                    Master switch — disable to turn off all automated emails for
                    this cohort
                  </Text>
                </Box>
                <Switch
                  checked={settings.master_enabled}
                  onChange={e => handleMasterToggle(e.currentTarget.checked)}
                  size="md"
                  color="medleyGreen"
                />
              </Group>
            </Paper>

            {/* MED-1307: per-cohort sender address (hidden if it failed to load) */}
            {senderSettings && (
              <Paper p="md" radius="md" withBorder>
                <TextInput
                  label="Send from address"
                  description={`Leave empty to send from ${senderSettings.default_sender_email}. ${senderSettings.allowed_domains.length === 1 ? 'Allowed domain' : 'Allowed domains'}: ${senderSettings.allowed_domains.join(', ')}`}
                  placeholder={senderSettings.default_sender_email}
                  value={senderInput}
                  onChange={e => {
                    setSenderInput(e.currentTarget.value);
                    setSenderError(null);
                  }}
                  error={senderError}
                  type="email"
                  autoComplete="off"
                />
              </Paper>
            )}

            {/* Persona sections */}
            {(Object.keys(PERSONA_CONFIG) as EmailPersona[]).map(persona => {
              const personaConfig = PERSONA_CONFIG[persona];
              const personaSettings = settings.personas[persona];
              const emailConfigs = EMAIL_CONFIG_BY_PERSONA[persona];
              // MED-896: a persona with no individual email toggles (e.g. Partners
              // after partner_survey_approval was removed) has nothing to control,
              // so its section is hidden entirely rather than rendered empty.
              if (Object.keys(emailConfigs).length === 0) return null;
              const masterDisabled = !settings.master_enabled;
              const featureLocked = isPersonaLockedByFeatureToggle(persona);
              const personaDisabled =
                masterDisabled || featureLocked || !personaSettings.enabled;

              return (
                <Paper
                  key={persona}
                  p="md"
                  radius="md"
                  withBorder
                  style={{ opacity: masterDisabled || featureLocked ? 0.5 : 1 }}
                >
                  {/* MED-778: Feature toggle lock banner */}
                  {featureLocked && (
                    <Alert
                      icon={<Lock size={16} />}
                      color="gray"
                      variant="light"
                      mb="sm"
                      py="xs"
                    >
                      <Text size="xs">
                        {personaConfig.label} email toggles are locked because{' '}
                        {personaConfig.label.toLowerCase()} features are
                        disabled for this cohort. Re-enable in Audience Feature
                        Controls above.
                      </Text>
                    </Alert>
                  )}

                  {/* Persona toggle */}
                  <Group justify="space-between" align="center">
                    <Box>
                      <Text fw={600}>{personaConfig.label}</Text>
                      <Text size="sm" c="dimmed">
                        {personaConfig.description}
                      </Text>
                    </Box>
                    <Switch
                      checked={personaSettings.enabled}
                      onChange={e =>
                        handlePersonaToggle(persona, e.currentTarget.checked)
                      }
                      disabled={masterDisabled || featureLocked}
                      size="md"
                      color="medleyGreen"
                    />
                  </Group>

                  <Divider my="sm" />

                  {/* Individual email toggles */}
                  <Stack gap="xs" pl="md">
                    {Object.entries(emailConfigs).map(([emailType, config]) => (
                      <Group
                        key={emailType}
                        justify="space-between"
                        align="center"
                        py={4}
                      >
                        <Box>
                          <Text size="sm" fw={500}>
                            {config.label}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {config.description}
                          </Text>
                        </Box>
                        <Switch
                          checked={
                            personaSettings.emails[
                              emailType as keyof typeof personaSettings.emails
                            ] ?? true
                          }
                          onChange={e =>
                            handleEmailTypeToggle(
                              persona,
                              emailType,
                              e.currentTarget.checked
                            )
                          }
                          disabled={personaDisabled}
                          size="sm"
                          color="medleyGreen"
                        />
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
}
